import { useAppUi } from '../../app-ui/AppUiScope';
import baseline, { appTextStyle } from '../../app-ui/baseline';
import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import Chevron from '../../components/content/Chevron';
import ContentShelf from '../../components/responsive/ContentShelf';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { adsApi } from '../../api/ads';
import { campaignDestination, safeExternalUrl, sponsoredRail } from './placement';
import { hideCampaign, useSponsoredPlacements } from './useSponsoredPlacements';
import SponsoredExposure from './SponsoredExposure';
import { AlbumCard } from '../../components/content/AlbumCard';
import './SponsoredContent.css';

function copy(locale) {
  return locale === 'en'
    ? {
        ad: 'Ad',
        info: 'About this ad',
        hide: 'Hide this ad',
        report: 'Report this ad',
        close: 'Close',
        reason: 'Why are you seeing this ad?',
        fallback: 'This content is sponsored.',
        submit: 'Send report',
        failed: 'Your report could not be sent. Please try again.',
        sending: 'Sending…',
        reasons: ['Inappropriate', 'Not relevant', 'Repetitive', 'Other'],
      }
    : {
        ad: '광고',
        info: '광고 정보',
        hide: '이 광고 숨기기',
        report: '광고 신고',
        close: '닫기',
        reason: '이 광고가 표시되는 이유',
        fallback: '광고주가 후원하는 콘텐츠입니다.',
        submit: '신고 보내기',
        failed: '신고를 보내지 못했어요. 다시 시도해 주세요.',
        sending: '보내는 중…',
        reasons: ['부적절한 내용', '관심 없는 내용', '반복되는 광고', '기타'],
      };
}
export function SponsoredLabel({
  campaign,
  placementKey,
  reasonChip = false,
  plain = false,
  compact = false,
}) {
  const { locale } = useWebLocale();
  const text = copy(locale),
    id = useId();
  const [open, setOpen] = useState(false),
    [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState('INAPPROPRIATE'),
    [saving, setSaving] = useState(false),
    [failed, setFailed] = useState(false);
  const close = () => {
    if (!saving) {
      setOpen(false);
      setReporting(false);
      setFailed(false);
    }
  };
  const submit = async () => {
    setSaving(true);
    setFailed(false);
    try {
      await adsApi.report(campaign.id, placementKey, reason);
      setOpen(false);
      hideCampaign(campaign.id);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <button
        className={`pp-sponsored-label${reasonChip ? ' pp-discovery-reason' : ''}${plain ? ' pp-sponsored-plain' : ''}${compact ? ' pp-sponsored-compact' : ''}`}
        type="button"
        aria-label={`${text.info} · ${campaign.sponsorName || campaign.headline}`}
        onClick={() => setOpen(true)}
      >
        {text.ad}
        {!plain && campaign.sponsorName ? ` · ${campaign.sponsorName}` : ''}
      </button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="xs" aria-labelledby={id}>
        <DialogTitle id={id}>{reporting ? text.report : text.info}</DialogTitle>
        <DialogContent>
          <strong>{campaign.sponsorName}</strong>
          <p>{text.reason}</p>
          <p>{campaign.whyThisAd || text.fallback}</p>
          {reporting && (
            <RadioGroup
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              aria-label={text.report}
            >
              {['INAPPROPRIATE', 'IRRELEVANT', 'REPETITIVE', 'OTHER'].map((value, index) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  disabled={saving}
                  control={<Radio />}
                  label={text.reasons[index]}
                />
              ))}
            </RadioGroup>
          )}
          {failed && <p role="alert">{text.failed}</p>}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', '& button': { minHeight: 44 } }}>
          {!reporting && (
            <>
              <Button
                onClick={() => {
                  setOpen(false);
                  hideCampaign(campaign.id);
                }}
              >
                {text.hide}
              </Button>
              <Button onClick={() => setReporting(true)}>{text.report}</Button>
            </>
          )}
          {reporting && (
            <Button disabled={saving} onClick={submit}>
              {saving ? text.sending : text.submit}
            </Button>
          )}
          <Button disabled={saving} onClick={close}>
            {text.close}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
export function CampaignLink({ campaign, placementKey, destination, className, children }) {
  const onClick = () => {
    void adsApi.click(campaign.id, placementKey).catch(() => {});
  };
  if (!destination) return <div className={className}>{children}</div>;
  return destination.startsWith('/') ? (
    <Link to={destination} className={className} onClick={onClick}>
      {children}
    </Link>
  ) : (
    <a
      href={destination}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
    >
      {children}
    </a>
  );
}
function CampaignAlbum({
  campaign,
  placementKey,
  album,
  label = true,
  reasonChip = false,
  showReleaseDate = false,
}) {
  return (
    <div className="pp-sponsored-album">
      <AlbumCard
        item={album}
        showRating={!label}
        showEmptyRating={!label}
        showAdMark={false}
        showReleaseDate={showReleaseDate}
        linkComponent={CampaignLink}
        linkProps={{ campaign, placementKey, destination: campaignDestination(campaign, album) }}
      />
      {label && (
        <SponsoredLabel campaign={campaign} placementKey={placementKey} reasonChip={reasonChip} />
      )}
    </div>
  );
}
export function SponsoredAlbum({
  campaign,
  placementKey,
  reasonChip = false,
  showReleaseDate = false,
}) {
  if (!campaign.albums?.length) return null;
  return (
    <SponsoredExposure campaignId={campaign.id} placementKey={placementKey}>
      <CampaignAlbum
        campaign={campaign}
        placementKey={placementKey}
        album={campaign.albums[0]}
        reasonChip={reasonChip}
        showReleaseDate={showReleaseDate}
      />
    </SponsoredExposure>
  );
}
export function SponsoredCollection({ campaign, placementKey }) {
  const appUi = useAppUi();
  const native = baseline.sponsored;
  if (!campaign.albums?.length) return null;
  return (
    <section
      className="pp-discovery-section pp-sponsored-collection"
      style={
        appUi ? { display: 'flex', flexDirection: 'column', gap: native.collection.gap } : undefined
      }
    >
      <SponsoredExposure campaignId={campaign.id} placementKey={placementKey}>
        <div
          className="pp-section-heading"
          style={appUi ? { minHeight: 0, marginBottom: 0 } : undefined}
        >
          <h2 style={appUi ? appTextStyle(baseline.tokens.typography.sectionTitle) : undefined}>
            <CampaignLink
              campaign={campaign}
              placementKey={placementKey}
              destination={safeExternalUrl(campaign.collectionLandingUrl)}
            >
              {campaign.headline}
              {safeExternalUrl(campaign.collectionLandingUrl) && (
                <Chevron className="pp-heading-chevron" />
              )}
            </CampaignLink>
          </h2>
        </div>
        <div
          className="pp-sponsored-description"
          style={
            appUi
              ? {
                  gap: native.collectionDescriptionRow.gap,
                  margin: `${native.collectionHeading.gap}px 0 0`,
                }
              : undefined
          }
        >
          <SponsoredLabel campaign={campaign} placementKey={placementKey} compact />
          {campaign.subheadline && (
            <p
              className="pp-secondary"
              style={appUi ? appTextStyle(baseline.contentText.metadata) : undefined}
            >
              {campaign.subheadline}
            </p>
          )}
        </div>
      </SponsoredExposure>
      <ContentShelf label={campaign.headline}>
        {campaign.albums.map((album, index) => (
          <CampaignAlbum
            key={`${album.id}-${index}`}
            campaign={campaign}
            placementKey={placementKey}
            album={album}
            label={false}
          />
        ))}
      </ContentShelf>
    </section>
  );
}
export function SponsoredCollections({ placementKey }) {
  const campaigns = useSponsoredPlacements(placementKey);
  return campaigns
    .filter((campaign) => campaign.placements[0]?.format === 'COLLECTION')
    .map((campaign) => (
      <SponsoredCollection key={campaign.id} campaign={campaign} placementKey={placementKey} />
    ));
}
export function SponsoredAlbumRail({ items = [], placementKey, label }) {
  const campaigns = useSponsoredPlacements(placementKey);
  const { t } = useWebLocale();
  const entries = sponsoredRail(items, campaigns);
  if (!entries.length) return <p className="pp-secondary">{t('empty')}</p>;
  return (
    <ContentShelf label={label}>
      {entries.map((entry) =>
        entry.kind === 'sponsored' ? (
          <SponsoredAlbum
            key={`ad-${entry.campaign.id}-${entry.occurrence}`}
            campaign={entry.campaign}
            placementKey={placementKey}
            showReleaseDate
            reasonChip
          />
        ) : (
          <AlbumCard
            key={`album-${entry.item.id || entry.item.albumId}`}
            item={entry.item}
            showReleaseDate
            showEmptyRating
          />
        ),
      )}
    </ContentShelf>
  );
}
